import { nanoid } from 'nanoid';
import type { Workflow, FlowNode, FlowEdge } from '../types';

function mkNode(
  id: string,
  kind: 'trigger' | 'wait' | 'condition' | 'action',
  position: { x: number; y: number },
  data: FlowNode['data']
): FlowNode {
  return { id, type: kind, position, data };
}

function mkEdge(source: string, target: string, label?: string): FlowEdge {
  return {
    id: `e-${source}-${target}`,
    source,
    target,
    label,
    type: 'smoothstep',
    animated: false,
    style: { stroke: '#C3A35B', strokeWidth: 2 },
    labelStyle: { fill: '#A4B1C4', fontSize: 10, fontWeight: 700 },
  };
}

export function templateStormReactivation(): Workflow {
  const trigger = nanoid(8);
  const cond = nanoid(8);
  const wait = nanoid(8);
  const actSms = nanoid(8);
  const actSkip = nanoid(8);
  return {
    id: 'tpl-storm-' + nanoid(8),
    name: 'Storm Reactivation',
    description:
      'Hail polygon overlaps a customer zip → SMS the dormant ones with a free-inspection link.',
    active: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    version: 1,
    nodes: [
      mkNode(trigger, 'trigger', { x: 60, y: 60 }, {
        kind: 'trigger',
        source: 'storm.hail_cell',
        label: 'Hail cell ≥ 1.5" in customer zip',
        description: 'NWS hail polygon feed via Storm Command',
      }),
      mkNode(cond, 'condition', { x: 60, y: 240 }, {
        kind: 'condition',
        label: 'Customer dormant & opted in',
        field: 'customer.last_serviced_months',
        operator: 'gte',
        value: '12',
        also: [
          { field: 'customer.sms_opt_in', operator: 'equals', value: 'true' },
        ],
      }),
      mkNode(actSkip, 'action', { x: 380, y: 240 }, {
        kind: 'action',
        type: 'log.audit',
        label: 'Skip · log to audit',
        body: 'Skip reason: no_match',
        description: 'No SMS sent, compliance log only',
      }),
      mkNode(actSms, 'action', { x: 60, y: 430 }, {
        kind: 'action',
        type: 'sms.send',
        label: 'Send branded storm SMS',
        to: '{{customer.phone}}',
        body: 'Heavy hail in your zip last night. Free inspection slot today: {{schedule_link}}',
      }),
      mkNode(wait, 'wait', { x: 60, y: 620 }, {
        kind: 'wait',
        label: 'Wait 3 days',
        amount: 3,
        unit: 'days',
        description: 'Before follow-up reminder',
      }),
    ],
    edges: [
      mkEdge(trigger, cond),
      mkEdge(cond, actSkip, 'no match'),
      mkEdge(cond, actSms, 'match'),
      mkEdge(actSms, wait),
    ],
  };
}

export function templateMissedCallTextback(): Workflow {
  const trigger = nanoid(8);
  const wait = nanoid(8);
  const act = nanoid(8);
  return {
    id: 'tpl-missed-' + nanoid(8),
    name: 'Missed-Call Text-Back',
    description: 'Caller rings, nobody answers, auto-SMS them with a schedule link inside 30 seconds.',
    active: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    version: 1,
    nodes: [
      mkNode(trigger, 'trigger', { x: 60, y: 60 }, {
        kind: 'trigger',
        source: 'call.missed',
        label: 'Inbound call missed',
        description: 'Caller rings ≥ 4 times with no answer',
      }),
      mkNode(wait, 'wait', { x: 60, y: 240 }, {
        kind: 'wait',
        label: 'Wait 30 seconds',
        amount: 30,
        unit: 'seconds',
        description: 'Avoid overlap with manual callback',
      }),
      mkNode(act, 'action', { x: 60, y: 410 }, {
        kind: 'action',
        type: 'sms.send',
        label: 'SMS reply',
        to: '{{caller.phone}}',
        body: 'Hey, sorry we missed your call! Tap to grab a time: {{schedule_link}}',
      }),
    ],
    edges: [mkEdge(trigger, wait), mkEdge(wait, act)],
  };
}

export function templatePaymentReminder(): Workflow {
  const trigger = nanoid(8);
  const cond = nanoid(8);
  const act = nanoid(8);
  return {
    id: 'tpl-payment-' + nanoid(8),
    name: 'Payment Reminder',
    description: 'Invoice 7 days old with balance > $50 → auto SMS reminder with Stripe link.',
    active: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    version: 1,
    nodes: [
      mkNode(trigger, 'trigger', { x: 60, y: 60 }, {
        kind: 'trigger',
        source: 'invoice.aged',
        label: 'Invoice 7 days old · unpaid',
        description: 'Nightly check 8 AM',
      }),
      mkNode(cond, 'condition', { x: 60, y: 240 }, {
        kind: 'condition',
        label: 'Balance > $50',
        field: 'invoice.balance_due',
        operator: 'gt',
        value: '50',
      }),
      mkNode(act, 'action', { x: 60, y: 410 }, {
        kind: 'action',
        type: 'sms.send',
        label: 'SMS reminder + Stripe link',
        to: '{{customer.phone}}',
        body: 'Friendly reminder — INV-{{invoice.number}} · ${{invoice.balance}} · pay here: {{stripe_link}}',
      }),
    ],
    edges: [mkEdge(trigger, cond), mkEdge(cond, act, 'true')],
  };
}

export function emptyWorkflow(): Workflow {
  return {
    id: 'wf-' + nanoid(8),
    name: 'New workflow',
    description: '',
    active: false,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    version: 1,
    nodes: [],
    edges: [],
  };
}

export const allTemplates = [
  templateStormReactivation,
  templateMissedCallTextback,
  templatePaymentReminder,
];
